import { useElementSize } from '@mantine/hooks';
import { type CSSProperties, type RefObject, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Callback } from 'ts/base/Callback';
import { useColorBlindModeEnabled } from 'ts/base/hooks/ColorBlindModeHook';
import { useKeyboardShortcut } from 'ts/base/hooks/UseKeyboardShortcut';
import { CODE_PADDING, getLineOffset } from 'ts/commons/code/RenderedCode';
import type { CodeContainerImperativeHandle, VisibleCodeRegion } from 'ts/commons/code/RenderedCode.context';
import { StringUtils } from 'ts/commons/StringUtils';
import type { DiffDescription } from 'typedefs/DiffDescription';
import { createStore, type StoreApi, useStore } from 'zustand';
import { CodeDiffOutlineBar, getChangeStyle } from './CodeDiffOutlineBar';
import { type CompareContentData, type CompareContentInfo, CompareContentSide } from './CompareContentSide';
import { CompareHeader } from './CompareHeader';
import styles from './CompareLayout.module.less';

type CompareLayoutProps = {
	leftContent: CompareContentInfo;
	rightContent: CompareContentInfo;
	leftData: CompareContentData;
	rightData: CompareContentData;
	diff: DiffDescription;
	showCoverage?: boolean;
	codeFontSize?: number;
	/** Will restrict the height of the code containers to the given height. */
	maxCodeHeight?: string;
	changeRegionsPreviousCommit?: number[];
	distributeOverTwoLines?: boolean;
	/**
	 * If true, only the range given in info#region will be visible in the compare view. If false (default) the full
	 * file will be visible, but changes will only be highlighted for the selected region.
	 */
	onlyShowRegion?: boolean;
	/** Scrolls the compare layout into view once it is rendered. This is used in the finding detail sibling tab. */
	scrollIntoViewOnMount?: boolean;
	isInconsistentClone?: boolean;
};

/**
 * Renders a compare view between two code files. In contrast to {@link CompareCodeComponent}, this class does not fetch
 * data by itself, so no dependency to the REST api exists.
 */
export function CompareLayout({
	diff,
	showCoverage = false,
	leftContent,
	rightContent,
	leftData,
	rightData,
	codeFontSize = 15,
	maxCodeHeight,
	changeRegionsPreviousCommit,
	distributeOverTwoLines,
	onlyShowRegion,
	scrollIntoViewOnMount,
	isInconsistentClone = false
}: CompareLayoutProps) {
	const [changeIndex, setChangeIndex] = useState<number | null>(null);

	const leftNumberOfLines = useMemo(
		() => (leftData.content ? StringUtils.splitLines(leftData.content.text).length : 1),
		[leftData.content]
	);
	const rightNumberOfLines = useMemo(
		() => (rightData.content ? StringUtils.splitLines(rightData.content.text).length : 1),
		[rightData.content]
	);

	const { leftInitialLine, rightInitialLine } = getInitialLinesFromDiff(
		diff,
		leftContent,
		rightContent,
		leftNumberOfLines,
		rightNumberOfLines
	);

	useKeyboardShortcut('Alt Shift N', 'Previous Diff', () =>
		getDiff(diff, changeIndex, -1, leftScrollHandleRef.current, setChangeIndex)
	);
	useKeyboardShortcut('Alt N', 'Next Diff', () =>
		getDiff(diff, changeIndex, 1, leftScrollHandleRef.current, setChangeIndex)
	);

	const leftScrollHandleRef = useRef<CodeContainerImperativeHandle | null>(null);
	const rightScrollHandleRef = useRef<CodeContainerImperativeHandle | null>(null);
	const skipScrollEventsRef = useRef(0);

	// We use a store to pass the offset from the event handler to the DiffConnector to avoid having to rerender this
	// component, which in turn also would lead to a re-render of the code context providers, which are very expensive
	const [offsetStore] = useState(() =>
		createStore(() => ({ leftOffsetY: CODE_PADDING, rightOffsetY: CODE_PADDING }))
	);

	const compareLayoutRef = useScrollIntoViewOnMount(scrollIntoViewOnMount);

	const leftVisibleRegion = onlyShowRegion ? leftContent.region : undefined;
	const rightVisibleRegion = onlyShowRegion ? rightContent.region : undefined;
	return (
		<div
			className={styles.compareLayout}
			style={{ '--max-code-height': maxCodeHeight } as CSSProperties}
			ref={compareLayoutRef}
		>
			<CompareHeader
				content={leftContent}
				logEntry={leftData.logEntry}
				side="left"
				distributeOverTwoLines={distributeOverTwoLines}
			/>
			<CompareHeader
				content={rightContent}
				logEntry={rightData.logEntry}
				side="right"
				distributeOverTwoLines={distributeOverTwoLines}
			/>
			<CodeDiffOutlineBar
				className={styles.leftOutline!}
				currentDiff={diff}
				changeLines={diff.leftChangeLines}
				numberOfLines={leftNumberOfLines}
				onDiffClick={lineNumber => leftScrollHandleRef.current?.scrollTo({ line: lineNumber, align: 'start' })}
			/>
			<div className={styles.leftCode} data-testid="left-code">
				<CompareContentSide
					side="left"
					info={leftContent}
					data={leftData}
					diffDescription={diff}
					initialLine={leftInitialLine}
					showTestCoverage={showCoverage}
					codeFontSize={codeFontSize}
					codeHandleRef={leftScrollHandleRef}
					visibleRegion={leftVisibleRegion}
					onScroll={() =>
						synchronizeScrollbars(
							diff.leftChangeLines,
							leftScrollHandleRef,
							diff.rightChangeLines,
							rightScrollHandleRef,
							skipScrollEventsRef,
							(leftOffsetY, rightOffsetY) => offsetStore.setState({ leftOffsetY, rightOffsetY })
						)
					}
				/>
			</div>
			<DiffConnector
				currentDiff={diff}
				offsetStore={offsetStore}
				fontSize={codeFontSize}
				leftVisibleRegion={leftVisibleRegion}
				rightVisibleRegion={rightVisibleRegion}
			/>
			<div className={styles.rightCode} data-testid="right-code">
				<CompareContentSide
					side="right"
					info={rightContent}
					data={rightData}
					diffDescription={diff}
					initialLine={rightInitialLine}
					showTestCoverage={showCoverage}
					codeFontSize={codeFontSize}
					codeHandleRef={rightScrollHandleRef}
					changeRegionsPreviousCommit={changeRegionsPreviousCommit}
					highlightInLastCommitChangedRegions={isInconsistentClone}
					visibleRegion={rightVisibleRegion}
					onScroll={() =>
						synchronizeScrollbars(
							diff.rightChangeLines,
							rightScrollHandleRef,
							diff.leftChangeLines,
							leftScrollHandleRef,
							skipScrollEventsRef,
							(rightOffsetY, leftOffsetY) => offsetStore.setState({ leftOffsetY, rightOffsetY })
						)
					}
				/>
			</div>
			<CodeDiffOutlineBar
				className={styles.rightOutline!}
				currentDiff={diff}
				changeLines={diff.rightChangeLines}
				numberOfLines={rightNumberOfLines}
				onDiffClick={lineNumber => rightScrollHandleRef.current?.scrollTo({ line: lineNumber, align: 'start' })}
			/>
		</div>
	);
}

/**
 * Scrolls either the left or the right code container to the initial diff. If an initialLine is set for either of the
 * code containers, this will be respected. Otherwise, the container in which the starting line of the diff is higher
 * (in respect to the lines of code in the container) will be scrolled.
 */
function getInitialLinesFromDiff(
	currentDiff: DiffDescription,
	leftCompareInfo: CompareContentInfo,
	rightCompareInfo: CompareContentInfo,
	leftContentNumberOfLines: number | null,
	rightContentNumberOfLines: number | null
): { leftInitialLine?: number; rightInitialLine?: number } {
	const leftInitialLine = leftCompareInfo.initialLine;
	const rightInitialLine = rightCompareInfo.initialLine;
	if (leftInitialLine !== undefined) {
		return { leftInitialLine };
	} else if (rightInitialLine !== undefined) {
		return { rightInitialLine };
	} else if (currentDiff.leftChangeLines.length > 0) {
		return scrollSideWhereChangeIsHigher(
			currentDiff,
			leftCompareInfo,
			rightCompareInfo,
			leftContentNumberOfLines,
			rightContentNumberOfLines
		);
	}
	return {};
}

/**
 * Scrolls to the first diff line on either the left or right code component, whichever has the higher starting line of
 * the diff.
 */
function scrollSideWhereChangeIsHigher(
	currentDiff: DiffDescription,
	leftCompareInfo: CompareContentInfo,
	rightCompareInfo: CompareContentInfo,
	leftContentNumberOfLines: number | null,
	rightContentNumberOfLines: number | null
): { leftInitialLine?: number; rightInitialLine?: number } {
	if (currentDiff.leftChangeLines.length === 0 || currentDiff.rightChangeLines.length === 0) {
		return {};
	}

	const sizeDelta = Math.abs(
		currentDiff.leftChangeLines[1]! -
			currentDiff.leftChangeLines[0]! -
			(currentDiff.rightChangeLines[1]! - currentDiff.rightChangeLines[0]!)
	);
	const firstLineOfTheDiffInLeftCodeComponent = currentDiff.leftChangeLines[0]! - sizeDelta;
	const firstLineOfTheDiffInRightCodeComponent = currentDiff.rightChangeLines[0]! - sizeDelta;

	if (leftCompareInfo.region === undefined || rightCompareInfo.region === undefined) {
		if (firstLineOfTheDiffInLeftCodeComponent > firstLineOfTheDiffInRightCodeComponent) {
			return { rightInitialLine: currentDiff.rightChangeLines[0]! - sizeDelta };
		} else {
			return { leftInitialLine: currentDiff.leftChangeLines[0]! - sizeDelta };
		}
	}

	const leftRelativeHeight =
		(currentDiff.leftChangeLines[0]! - leftCompareInfo.region.startLine) / (leftContentNumberOfLines ?? 1);
	const rightRelativeHeight =
		(currentDiff.rightChangeLines[0]! - rightCompareInfo.region.startLine) / (rightContentNumberOfLines ?? 1);

	if (leftRelativeHeight > rightRelativeHeight) {
		return { leftInitialLine: firstLineOfTheDiffInLeftCodeComponent };
	} else {
		return { rightInitialLine: firstLineOfTheDiffInRightCodeComponent };
	}
}

/** Adds listeners to the scrollbars of the code views to allow synchronized scrolling. */
function synchronizeScrollbars(
	sourceChangeLines: number[],
	sourceHandleRef: RefObject<CodeContainerImperativeHandle | null>,
	targetChangeLines: number[],
	targetHandleRef: RefObject<CodeContainerImperativeHandle | null>,
	skipScrollEvents: RefObject<number>,
	setOffsets: (sourceOffsetY: number, targetOffsetY: number) => void,
	frames = 0
): void {
	const sourceHandle = sourceHandleRef.current;
	const targetHandle = targetHandleRef.current;
	if (sourceHandle == null || targetHandle == null) {
		if (frames > 20) {
			// Avoid runaway recursion
			return;
		}
		// If we don't have the handle yet delay handling the event to the next frame
		requestAnimationFrame(() =>
			synchronizeScrollbars(
				sourceChangeLines,
				sourceHandleRef,
				targetChangeLines,
				targetHandleRef,
				skipScrollEvents,
				setOffsets,
				frames + 1
			)
		);
		return;
	}

	if (skipScrollEvents.current > 0) {
		skipScrollEvents.current--;
		return;
	}

	// First find line number of line shown at the middle
	const sourceMiddleLine = sourceHandle.getCenterLine();

	const targetMiddleLine = determineTargetMiddleLine(
		sourceMiddleLine,
		sourceChangeLines,
		sourceHandle.getVisibleRegion(),
		targetChangeLines,
		targetHandle.getVisibleRegion()
	);

	// Synchronize both vertical and horizontal scrolling. Each operation fires a scroll event on the target
	// only if the value actually changes, so we count how many events to expect and skip that many to prevent
	// a cascading feedback loop (TS-44993).
	const targetScrollOffsetY = targetHandle.scrollTo({ line: targetMiddleLine });
	if (targetScrollOffsetY !== targetHandle.getScrollOffsetY()) {
		skipScrollEvents.current++;
	}
	const sourceScrollLeft = sourceHandle.getScrollOffsetX();
	if (sourceScrollLeft !== targetHandle.getScrollOffsetX()) {
		targetHandle.scrollToOffsetX(sourceScrollLeft);
		skipScrollEvents.current++;
	}

	setOffsets(CODE_PADDING - sourceHandle.getScrollOffsetY(), CODE_PADDING - targetScrollOffsetY);
}

function useScrollIntoViewOnMount(scrollIntoViewOnMount: undefined | boolean) {
	const compareLayoutRef = useRef<HTMLDivElement | null>(null);
	useLayoutEffect(() => {
		if (scrollIntoViewOnMount) {
			compareLayoutRef.current?.scrollIntoView();
		}
	}, [scrollIntoViewOnMount]);
	return compareLayoutRef;
}

type DiffConnectorProps = {
	currentDiff: DiffDescription;
	offsetStore: StoreApi<{ leftOffsetY: number; rightOffsetY: number }>;
	fontSize: number;
	leftVisibleRegion?: VisibleCodeRegion;
	rightVisibleRegion?: VisibleCodeRegion;
};

function DiffConnector({
	currentDiff,
	offsetStore,
	fontSize,
	leftVisibleRegion,
	rightVisibleRegion
}: DiffConnectorProps) {
	const { ref, width, height } = useElementSize();
	const colorBlindModeEnabled = useColorBlindModeEnabled();
	const { leftOffsetY, rightOffsetY } = useStore(offsetStore);

	return (
		<div className={styles.diff} ref={ref}>
			<svg>
				{Array.from({ length: currentDiff.leftChangeLines.length / 2 }, (_, i) => {
					const index = i * 2;

					let leftStartLine = currentDiff.leftChangeLines[index]!;
					let rightStartLine = currentDiff.rightChangeLines[index]!;
					let leftEndLine = currentDiff.leftChangeLines[index + 1]!;
					let rightEndLine = currentDiff.rightChangeLines[index + 1]!;

					leftStartLine = getLineOffset(fontSize, leftStartLine, leftOffsetY, leftVisibleRegion);
					leftEndLine = getLineOffset(fontSize, leftEndLine, leftOffsetY, leftVisibleRegion);
					rightStartLine = getLineOffset(fontSize, rightStartLine, rightOffsetY, rightVisibleRegion);
					rightEndLine = getLineOffset(fontSize, rightEndLine, rightOffsetY, rightVisibleRegion);

					// Skip if outside visible area
					if (leftEndLine < 0 && rightEndLine < 0) {
						return null;
					}
					if (leftStartLine >= height && rightStartLine >= height) {
						return null;
					}

					const points = [
						`-1,${leftStartLine}`, // Left top
						`${width + 1},${rightStartLine}`, // Right top
						`${width + 1},${rightEndLine}`, // Right bottom
						`-1,${leftEndLine}` // Left bottom
					].join(' ');

					const style = getChangeStyle(index, currentDiff, colorBlindModeEnabled);

					return (
						<polygon
							key={index}
							points={points}
							fill={style.background}
							stroke={style.color}
							strokeWidth={1}
						/>
					);
				})}
			</svg>
		</div>
	);
}

/**
 * Scrolls to the next or previous diff, depending on the parameter
 *
 * @param next If true scrolls to the next diff, if false scrolls to the previous diff
 */
function getDiff(
	diff: DiffDescription,
	diffIndex: number | null,
	direction: 1 | -1,
	leftHandle: CodeContainerImperativeHandle | null,
	setChangeIndex: Callback<number>
): void {
	let newDiffIndex = 0;
	if (diffIndex != null) {
		newDiffIndex = (diffIndex + diff.leftChangeLines.length + direction) % (diff.leftChangeLines.length / 2);
	}
	setChangeIndex(newDiffIndex);
	const lineNumber = diff.leftChangeLines[newDiffIndex * 2]!;
	leftHandle?.scrollTo({ line: lineNumber });
}

/** Determines the line number to be displayed in the middle of the target code. */
function determineTargetMiddleLine(
	sourceMiddleLine: number,
	sourceChangeLines: number[],
	sourceRegion: VisibleCodeRegion,
	targetChangeLines: number[],
	targetRegion: VisibleCodeRegion
): number {
	let sourceTop = sourceRegion.startLine;
	let targetTop = targetRegion.startLine;
	let sourceBottom: number | null = null;
	let targetBottom: number | null = null;
	for (let i = 0; i < sourceChangeLines.length; ++i) {
		if (sourceMiddleLine < sourceChangeLines[i]!) {
			sourceBottom = sourceChangeLines[i]!;
			targetBottom = targetChangeLines[i]!;
			break;
		}
		sourceTop = sourceChangeLines[i]!;
		targetTop = targetChangeLines[i]!;
	}
	if (sourceBottom == null || targetBottom == null) {
		sourceBottom = sourceRegion.endLine + 1;
		targetBottom = targetRegion.endLine + 1;
	}

	// Perform linear interpolation in corresponding interval
	const sourceRange = sourceBottom - sourceTop;
	const targetRange = targetBottom - targetTop;

	return targetTop + ((sourceMiddleLine - sourceTop) / sourceRange) * targetRange;
}
